import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { connect } from "../lib/direct-link";
import { describePath } from "../lib/connection-path";
import { readRoomFromHash } from "../lib/signalling";
import { holdTunnel } from "../lib/tunnel-link";
import { acceptPairing, nameFor } from "../lib/tunnel-signin";
import { canPair } from "../lib/device-key";
import { useAuth } from "../contexts/AuthContext";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The phone arriving at its owner's Mac.
 *
 * Reached by scanning the code on the desktop. It opens the channel, points
 * the whole app's calls down it, and then gets out of the way: what the user
 * sees next is Novamund, their own conversations and their own models, from
 * the machine in the other room.
 *
 * This used to be a demo chat that proved the channel carried text. It did,
 * and that is why this page can now be a doorway instead of a destination.
 *
 * The connection is deliberately not closed when this page unmounts. It has
 * been handed to the app by then, and closing it on the way out would tear
 * down the thing the user just connected.
 */
export default function JoinPage() {
  const { t } = useTaal();
  const [phase, setPhase] = useState("starting");
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [route, setRoute] = useState(null);
  const [signedIn, setSignedIn] = useState(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const link = useRef(null);
  const handedOver = useRef(false);
  const started = useRef(false);

  const run = useCallback(async () => {
    const room = readRoomFromHash(window.location.hash);
    if (!room) {
      setPhase("error");
      setError("This link is missing its connection details. Scan the code on your computer again.");
      return;
    }
    try {
      const opened = await connect({
        role: "guest",
        room: room.room,
        secret: room.secret,
        // Fifteen seconds is enough for two laptops on one wifi and not
        // enough for a phone on mobile data that has to reserve an address
        // on the relay first. The desktop side waits two minutes; there is
        // no reason for this side to give up long before it does.
        timeoutMs: 45000,
      });
      link.current = opened;
      setPhase("connected");
      opened.path().then(setRoute);

      // From here on every call the app makes goes down this channel. The
      // desktop sends the pairing offer moments later, unasked.
      //
      // Held rather than merely used: this page navigates away in a moment,
      // and whatever notices that the channel died has to outlive it.
      const client = holdTunnel(opened, {
        onPairing: async ({ offer, secret }) => {
          try {
            if (!canPair(window.crypto)) {
              // No WebCrypto: an old browser, or a page opened over plain
              // http. The phone cannot make a key, so it cannot be signed
              // in, and saying so beats a message about "undefined".
              throw new Error("This browser cannot make a key here.");
            }
            const signedIn = await acceptPairing({
              offer,
              secret,
              subtle: window.crypto.subtle,
              storage: window.localStorage,
              name: nameFor(navigator.userAgent),
              call: ({ method, path, body }) =>
                client
                  .request({
                    method,
                    path,
                    headers: { "content-type": "application/json" },
                    body: body ? JSON.stringify(body) : null,
                  })
                  .then(async (answer) => JSON.parse((await answer.text()) || "{}")),
            });
            setSignedIn(signedIn);
            // The auth context asks who you are once, on mount, and by now
            // that has already happened with no token. Without this the
            // phone has real data and a corner saying nobody is signed in.
            if (signedIn) await refreshUser?.();
          } catch {
            setSignedIn(false);
          }
          // Either way the app opens. Signed in it shows this Mac's own
          // conversations; not signed in it shows a sign-in screen, which is
          // the truth rather than a screen full of failed calls.
          navigate("/", { replace: true });
        },
      });
      handedOver.current = true;

      // If no offer arrives, open the app anyway rather than waiting on a
      // desktop that may be an older build.
      setTimeout(() => navigate("/", { replace: true }), 12000);
    } catch (e) {
      setPhase("error");
      setError(e.message || t("Er kon geen directe verbinding worden geopend."));
      // What it managed before it stopped. The phone has no console, so if
      // this is not on the screen it does not exist.
      setDetail(e.attempt || null);
    }
  }, [navigate, refreshUser, t]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
    return () => {
      if (!handedOver.current) link.current?.close();
    };
  }, [run]);

  return (
    <div className="min-h-screen flex flex-col bg-background px-4 pt-[var(--veilig-boven)] pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-sm mx-auto flex-1 flex flex-col justify-center py-8" data-testid="join-page">
        <div className="flex justify-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
            {phase === "connected" ? (
              <ShieldCheck className="h-7 w-7 text-ok" />
            ) : phase === "error" ? (
              <AlertTriangle className="h-7 w-7 text-corona" />
            ) : (
              <Radio className="h-7 w-7 text-[hsl(var(--accent))]" />
            )}
          </div>
        </div>

        {phase === "starting" && (
          <p
            className="text-sm text-muted-foreground text-center flex items-center justify-center gap-2"
            data-testid="join-starting"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("Je computer zoeken...")}
          </p>
        )}

        {phase === "error" && (
          <>
            <p className="text-sm text-corona text-center" data-testid="join-error">
              {error}
            </p>
            {detail && (
              <p className="text-[11px] text-muted-foreground text-center mt-3 px-2" data-testid="join-detail">
                {detail}
              </p>
            )}
          </>
        )}

        {phase === "connected" && (
          <>
            <p className="text-sm text-ok text-center" data-testid="join-connected">
              {signedIn === null
                ? t("Verbonden met je Mac. Je wordt aangemeld.")
                : signedIn
                ? t("Aangemeld op je Mac. Novamund wordt geopend.")
                : t("Verbonden met je Mac. Deze telefoon is niet aangemeld.")}
            </p>
            <p className="text-[11px] text-muted-foreground text-center mt-2" data-testid="join-route">
              {route ? describePath(route) : " "}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
